import { useMemo, useState } from "react";
import { ChevronRight, Search } from "lucide-react";
import type { BillingAccount, BillingPlan, MrrPoint } from "@shared/lib/admin-billing";
import type { InvoiceRecord } from "@shared/lib/admin-db";
import { formatCents, formatNumber } from "@shared/lib/format";
import { AdminEmpty, AdminPanel, StatCard, StatGroup, StatusDot } from "@shared/components/blocks";
import { Input } from "@shared/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@shared/components/ui/table";
import { BillingAccountDialog } from "./billing-account-dialog";
import { AccountAvatar, UsageMeter, displayName, priceLabel, segmentOf, stripeState } from "./billing-parts";
import { ManualInvoices } from "./manual-invoices";
import { SegmentedFilter } from "./segmented-filter";
import { MrrChart } from "./mrr-chart";

const plural = (count: number, word: string) => `${formatNumber(count)} ${word}${count > 1 ? "s" : ""}`;

export function BillingView({
  accounts,
  plans,
  mrr,
  invoices,
  clients,
}: {
  accounts: BillingAccount[];
  plans: BillingPlan[];
  mrr: MrrPoint[];
  invoices: InvoiceRecord[];
  clients: Array<{ id: string; name: string }>;
}) {
  const [segment, setSegment] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [now] = useState(() => Date.now());

  const stats = useMemo(() => {
    const customers = accounts.filter((account) => !account.is_admin);
    const paying = customers.filter((account) => account.mrr_cents > 0);
    const current = mrr.at(-1)?.total ?? paying.reduce((sum, account) => sum + account.mrr_cents, 0);
    const previous = mrr.at(-2)?.total ?? 0;
    const aiCost = accounts.reduce((sum, account) => sum + account.ai_cost_cents, 0);
    const recent = customers.filter((account) => now - new Date(account.created_at).getTime() < 30 * 86_400_000);
    const pastDue = customers.filter((account) => account.stripe_status === "past_due").length;
    const ending = customers.filter((account) => account.cancel_at_period_end && account.stripe_status === "active").length;
    const atLimit = customers.filter(
      (account) => account.mrr_cents === 0 && account.offers_used >= account.max_offers && account.passes >= account.pass_capacity,
    ).length;
    return { customers: customers.length, paying: paying.length, recent: recent.length, current, previous, aiCost, pastDue, ending, atLimit };
  }, [accounts, mrr, now]);

  const segments = useMemo(() => {
    const count = (key: string) => accounts.filter((account) => segmentOf(account) === key).length;
    return [
      { key: "all", label: "Tous", count: accounts.length },
      ...plans.map((plan) => ({ key: plan.id, label: plan.name, count: count(plan.id) })),
      { key: "admin", label: "Admins", count: count("admin") },
    ].filter((item) => item.key === "all" || item.count > 0);
  }, [accounts, plans]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return accounts.filter((account) => {
      if (segment !== "all" && segmentOf(account) !== segment) return false;
      if (!term) return true;
      return [account.email, account.full_name, account.client?.name ?? ""].some((value) => value.toLowerCase().includes(term));
    });
  }, [accounts, segment, search]);

  const selected = accounts.find((account) => account.user_id === selectedId) ?? null;
  const delta = stats.current - stats.previous;

  return (
    <>
      <StatGroup columns={5}>
        <StatCard label="Comptes" note={`${formatNumber(stats.recent)} nouveau${stats.recent > 1 ? "x" : ""} sur 30 jours`} value={formatNumber(stats.customers)} />
        <StatCard
          label="Abonnés payants"
          note={stats.customers ? `${Math.round((stats.paying / stats.customers) * 100)} % des comptes` : "—"}
          value={formatNumber(stats.paying)}
        />
        <StatCard
          label="MRR"
          note={delta === 0 ? "stable sur un mois" : `${delta > 0 ? "+" : "−"}${formatCents(Math.abs(delta))} sur un mois`}
          value={formatCents(stats.current)}
        />
        <StatCard
          label="Coût IA (30 j)"
          note={stats.current > 0 ? `${Math.round((stats.aiCost / stats.current) * 100)} % du MRR` : "aucun revenu récurrent"}
          value={formatCents(stats.aiCost)}
        />
        <StatCard
          label="À surveiller"
          note={`${stats.pastDue} en relance · ${stats.ending} résiliation${stats.ending > 1 ? "s" : ""} prévue${stats.ending > 1 ? "s" : ""}`}
          tint={stats.pastDue + stats.ending > 0 ? "rose" : undefined}
          value={formatNumber(stats.pastDue + stats.ending)}
        />
      </StatGroup>

      <AdminPanel
        action={<span className="text-[12.5px] text-muted-foreground">Fin de mois, par plan</span>}
        className="mt-4.5"
        title="MRR sur douze mois"
      >
        <MrrChart plans={plans} points={mrr} />
      </AdminPanel>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative w-full sm:w-72">
          <Search aria-hidden="true" className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            aria-label="Rechercher un compte"
            className="h-9 pl-9"
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un compte…"
            value={search}
          />
        </div>
        <SegmentedFilter items={segments} label="Filtrer par plan" onChange={setSegment} value={segment} />
        {stats.atLimit > 0 && (
          <span className="text-[12.5px] text-muted-foreground">
            {plural(stats.atLimit, "compte")} gratuit{stats.atLimit > 1 ? "s" : ""} au bout de {stats.atLimit > 1 ? "leur" : "son"} plan
          </span>
        )}
      </div>

      <AdminPanel className="mt-3" flush>
        {shown.length === 0 ? (
          <AdminEmpty>{accounts.length === 0 ? "Aucun compte pour l’instant." : "Aucun compte ne correspond."}</AdminEmpty>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="pl-5">Compte</TableHead>
                <TableHead className="max-sm:hidden">Plan</TableHead>
                <TableHead className="max-md:hidden">Offres</TableHead>
                <TableHead className="max-md:hidden">Passages (30 j)</TableHead>
                <TableHead className="text-right max-lg:hidden">Leads (30 j)</TableHead>
                <TableHead className="text-right max-lg:hidden">Coût IA (30 j)</TableHead>
                <TableHead className="max-sm:hidden">Stripe</TableHead>
                <TableHead className="w-10 pr-4"><span className="sr-only">Ouvrir</span></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.map((account) => {
                const state = stripeState(account);
                const price = priceLabel(account);
                return (
                  <TableRow
                    className="cursor-pointer"
                    key={account.user_id}
                    onClick={() => setSelectedId(account.user_id)}
                  >
                    <TableCell className="pl-5">
                      <div className="flex min-w-0 items-center gap-3">
                        <AccountAvatar account={account} className="max-sm:hidden" />
                        <div className="min-w-0">
                          <button
                            className="block max-w-[280px] cursor-pointer truncate text-left font-semibold hover:underline"
                            onClick={(event) => {
                              event.stopPropagation();
                              setSelectedId(account.user_id);
                            }}
                            type="button"
                          >
                            {account.client?.name ?? displayName(account)}
                          </button>
                          <span className="block max-w-[280px] truncate text-[12.5px] text-muted-foreground">{account.email}</span>
                          <span className="mt-0.5 block text-[12.5px] text-muted-foreground sm:hidden">
                            {account.plan_name} · {account.offers_used}/{account.max_offers} offre{account.max_offers > 1 ? "s" : ""} · {state.label}
                          </span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-sm:hidden">
                      <span className="font-medium">{account.is_admin ? "Admin" : account.plan_name}</span>
                      <span className="block text-[12.5px] text-muted-foreground">
                        {price ?? (account.is_admin ? `limites ${account.plan_name}` : `veille tous les ${account.min_frequency_days} j`)}
                      </span>
                    </TableCell>
                    <TableCell className="max-md:hidden">
                      <UsageMeter max={account.max_offers} used={account.offers_used} />
                    </TableCell>
                    <TableCell className="max-md:hidden">
                      <UsageMeter max={account.pass_capacity} used={account.passes} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums max-lg:hidden">{formatNumber(account.leads)}</TableCell>
                    <TableCell className="text-right tabular-nums max-lg:hidden">{formatCents(account.ai_cost_cents)}</TableCell>
                    <TableCell className="max-sm:hidden">
                      <StatusDot tone={state.tone}>{state.label}</StatusDot>
                    </TableCell>
                    <TableCell className="pr-4">
                      <ChevronRight aria-hidden="true" className="size-4 text-subtle-foreground" />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </AdminPanel>

      <ManualInvoices className="mt-6" clients={clients} invoices={invoices} />

      <BillingAccountDialog account={selected} onClose={() => setSelectedId(null)} />
    </>
  );
}
